import { ref } from 'vue';

/**
 * Liveness of the single graphql-ws socket, exposed as module state so screens
 * can react to it without reaching into the Apollo link.
 *
 * Why this exists: Postgres `NOTIFY` (and therefore every GraphQL subscription
 * in this app) is fire-and-forget with no backlog. Anything published while the
 * socket was down — a sleeping PWA, a closed laptop lid, an API instance swap —
 * is gone for good. So a reconnect is not just a transport detail: it is the
 * signal that a screen's data may have silently drifted and has to be re-pulled.
 *
 * `wsReconnectEpoch` deliberately counts only *re*-connections: the very first
 * `connected` is the page's initial load, which already fetched everything.
 */
export const wsConnected = ref(false);

/** Incremented on every reconnect after the initial connect. Never decremented. */
export const wsReconnectEpoch = ref(0);

let seenFirstConnect = false;

/** Wired into `createClient({ on: { connected } })` — see `client.ts`. */
export const markWsConnected = (): void => {
  wsConnected.value = true;

  if (seenFirstConnect) {
    wsReconnectEpoch.value += 1;

    return;
  }

  seenFirstConnect = true;
};

/** Wired into `createClient({ on: { closed } })` — see `client.ts`. */
export const markWsClosed = (): void => {
  wsConnected.value = false;
};
